import {startCase} from 'lodash-es';
import dayjs from 'utils/dayjs';
import formatDuration from 'utils/format-duration';
import {DAYS_WITH_LABELS} from 'utils/date-time';
import {CATEGORIZED_ALGORITHMS} from 'constants/index';

/**
 * The possible configurations for flex rates durations
 * @type {{'30min': number, '60min': number}}
 */
export const FLEX_RATES_CONFIG_DURATION_IN_MIN = {
    '30min': 30,
    '60min': 60,
};

/**
 * Determines whether or not the facility is using flex rates. This is determined by the presence of the `matrix_smart_rates` pricing algorithm. The array of algorithms will contain all applicable pricing algorithms for the facility, if the `rate_experiment` algorithm is present, then all of the included pricing algorithms will also be present in the array.
 *
 * @function isUsingFlexRates
 * @param {Array<string>} transientPricingAlgorithms - An Array of pricing algorithms as strings
 * @returns {boolean} A boolean indicating whether or not the facility is using flex rates
 * @example
 * const transientPricingAlgorithms = ['matrix_smart_rates', 'matrix_iq', 'rate_experiment'];
 * const isFlexRates = isUsingFlexRates(transientPricingAlgorithms);
 * // isFlexRates === true
 *
 * @example
 * const transientPricingAlgorithms = ['matrix_iq', 'legacy', 'rate_experiment'];
 * const isFlexRates = isUsingFlexRates(transientPricingAlgorithms);
 * // isFlexRates === false
 */
export const isUsingFlexRates = (transientPricingAlgorithms = []) => {
    return Boolean(
        transientPricingAlgorithms.filter((pricingAlgorithm) =>
            CATEGORIZED_ALGORITHMS.flexRates.includes(pricingAlgorithm)
        ).length
    );
};

/**
 * Determines whether or not the facility is using IQ rates. This is determined by the presence of the `matrix_precision_pricing` pricing algorithm. The array of algorithms will contain all applicable pricing algorithms for the facility, if the `rate_experiment` algorithm is present, then all of the included pricing algorithms will also be present in the array.
 *
 * @function isUsingSpotHeroIQ
 * @param {Array<string>} transientPricingAlgorithms - An Array of pricing algorithms as strings
 * @returns {boolean} A boolean indicating whether or not the facility is using flex rates
 * @example
 * const transientPricingAlgorithms = ['matrix_smart_rates', 'matrix_iq', 'rate_experiment'];
 * const isSpotHeroIQ = isUsingSpotHeroIQ(transientPricingAlgorithms);
 * // isSpotHeroIQ === false
 *
 * @example
 * const transientPricingAlgorithms = ['matrix_iq', 'legacy', 'rate_experiment'];
 * const isSpotHeroIQ = isUsingSpotHeroIQ(transientPricingAlgorithms);
 * // isSpotHeroIQ === true
 */
export const isUsingSpotHeroIQ = (transientPricingAlgorithms = []) => {
    return Boolean(
        transientPricingAlgorithms.filter((pricingAlgorithm) =>
            CATEGORIZED_ALGORITHMS.spotHeroIQ.includes(pricingAlgorithm)
        ).length
    );
};

/**
 * Determines whether or not the facility is using standard rates. Standard rates are determined by the presence of a `legacy` pricing algorithm. The array of algorithms will contain all applicable pricing algorithms for the facility, if the `legacy` algorithm is present, then the facility is using standard rates.
 *
 * @function isUsingStandardRates
 * @param {Array<string>} transientPricingAlgorithms - An array of pricing algorithms as strings.
 * @returns {boolean} A boolean indicating whether or not the facility is using standard rates
 * @example
 * const transientPricingAlgorithms = ['legacy'];
 * const isStandardRates = isUsingStandardRates(transientPricingAlgorithms);
 * // isStandardRates === true
 *
 * @example
 * const transientPricingAlgorithms = ['matrix_smart_rates', 'matrix_iq', 'rate_experiment'];
 * const isStandardRates = isUsingStandardRates(transientPricingAlgorithms);
 * // isStandardRates === false
 */
export const isUsingStandardRates = (transientPricingAlgorithms = []) => {
    return Boolean(
        transientPricingAlgorithms.filter((pricingAlgorithm) =>
            CATEGORIZED_ALGORITHMS.legacy.includes(pricingAlgorithm)
        ).length
    );
};

/**
 * Duration labels across 24 hours formatted as "1 hr", "2 hr", etc.
 * @type {object}
 *
 * @example
 * DURATION_LABELS['01:00'] // 1 hr
 */
export const DURATION_LABELS = Array.from({length: 49}, (_, i) => i / 2).reduce(
    (acc, duration) => {
        const hours = Math.floor(duration);
        const minutes = (duration % 1) * 60;
        const formattedDuration = formatDuration(hours, minutes);

        return {
            ...acc,
            [formattedDuration]: `${duration} ${duration === 1 ? 'hr' : 'hrs'}`,
        };
    },
    {}
);

/**
 * Formats a multi-day duration string into a more human-readable format.
 * @param {string} duration - The duration string in the format "HH:MM"
 * @returns {{durationAsDays: string, durationAsHours: string}}
 *
 * @example
 * formatMultiDayDuration("48:00") => {durationAsDays: "2 days", durationAsHours: "48h"}
 */
export const formatMultiDayDuration = (duration) => {
    const numberOfDays = duration.split(':')[0] / 24;
    const numberOfHours = duration.split(':')[0];
    const isMoreThanOneDay = numberOfDays > 1;
    const durationAsDays = `${numberOfDays} day${isMoreThanOneDay ? 's' : ''}`;
    const durationAsHours = `${numberOfHours}h`;

    return {
        durationAsDays,
        durationAsHours,
    };
};

/**
 * Duration labels for the multi-day discounts table, formatted as "2+ days (48h+)", "3+ days (72h+)", etc.
 * @type {object}
 *
 * @example
 * MULTI_DAY_DISCOUNT_DURATION_LABELS['48:00'] // 2+ days (48h+)
 */
export const MULTI_DAY_DISCOUNT_DURATION_LABELS = {
    '24:00': '>1 day (>24h)',
    '48:00': '2+ days (48h+)',
    '72:00': '3+ days (72h+)',
    '120:00': '5+ days (120h+)',
    '168:00': '7+ days (168h+)',
    '336:00': '14+ days (336h+)',
    '672:00': '28+ days (672h+)',
};

/**
 * Parses a 48-hour clock time string into a human readable string.  e.g
 * @param {string} timeString - The time string to format
 * @return {string}
 *
 *  @example
 *  "03:00" => "3:00 AM"
 *  "27:00" => "3:00 AM (next day)"
 */
export const parse48HourClockTime = (timeString) => {
    // Split the timeString into hours
    const hoursOffset = Number(timeString.split(':')[0]);
    const minutesOffset = Number(timeString.split(':')[1]);
    // Calculate the day offset
    const dayOffset = Math.floor(hoursOffset / 24);
    const hours = hoursOffset % 24;
    const time = dayjs().hour(hours).minute(minutesOffset);
    // Format the time in 12-hour format
    const formattedTimeString = time.format('h:mm A');

    // Return the new time string with the day information
    if (dayOffset === 0) {
        return formattedTimeString;
    }

    if (dayOffset === 1) {
        return `${formattedTimeString} (next day)`;
    }

    // Handle the rare case where the hours go beyond 48
    return `${formattedTimeString} (${dayOffset} days later)`;
};

/**
 * Converts a time string to decimal representation.
 *
 * @param {string} time - The time in 'HH:MM' format (24-hour clock).
 * @returns {number} - The decimal representation of the time.
 */
export const convertTimeToDecimal = (time) => {
    const [hours, minutes] = time.split(':').map(Number);

    return hours + minutes / 60;
};

/**
 * Calculates the end time by adding a duration to the start time.  The helper function is necessary due to using a 48-hour clock.
 *
 * @param {string} start - The start time in the format 'HH:MM'.
 * @param {number} duration - The duration to add (in hours).
 * @returns {string} - The end time in the format 'HH:MM'.
 */
export const addTime = (start, duration) => {
    const [startHour, startMinutes] = start.split(':').map(Number);
    let totalHours = startHour + duration;
    let totalMinutes = startMinutes;

    if (totalMinutes >= 60) {
        totalHours++;
        totalMinutes -= 60;
    }

    return `${String(totalHours).padStart(2, '0')}:${String(
        totalMinutes
    ).padStart(2, '0')}`;
};

/**
 * Generates the description for Rate Segments
 *
 * @param {string} earliestEntryTime
 * @param {string} latestEntryTime
 * @param {string} minDuration
 * @param {string} maxDuration
 * @param {string} triggerTime
 * @param {string} [triggerType]
 * @return {string}
 *
 * @example
 * generateRateSegmentDescription({
 *    earliestEntryTime: '03:00',
 *    latestEntryTime: '06:00',
 *    minDuration: '01:00',
 *    maxDuration: '02:00',
 *    triggerTime: '03:00',
 *    triggerType: 'earliestEntryTime',
 *    }) // "Starts Between: 3:00 AM and 6:00 AM | Duration: 1 hr - 2 hrs | Earliest Entry Time: 3:00 AM"
 */
export const generateRateSegmentDescription = ({
    earliestEntryTime,
    latestEntryTime,
    minDuration,
    maxDuration,
    triggerTime,
    triggerType,
}) => {
    return `Starts Between: ${parse48HourClockTime(
        earliestEntryTime
    )} and ${parse48HourClockTime(latestEntryTime)} | Duration: ${
        DURATION_LABELS[minDuration]
    } - ${DURATION_LABELS[maxDuration]} ${
        triggerType
            ? `| ${startCase(triggerType)}: ${parse48HourClockTime(
                  triggerTime
              )}`
            : ''
    }`;
};

/**
 * Formats the days of the week for the Rate Segment in read only mode to a comma separated list in calendar order
 * @param {DAYS_OF_WEEK[]} daysOfWeek
 * @return {string} - Comma separated list of days of the week
 *
 * @example
 * formatDaysOfWeekReadOnly(['Monday', 'Wednesday', 'Tuesday']) // "mon, tue, wed"
 */
export const formatDaysOfWeekReadOnly = (daysOfWeek) => {
    const formattedValue = daysOfWeek
        .map((days) => days.toLowerCase())
        .sort(
            (a, b) =>
                Object.keys(DAYS_WITH_LABELS).indexOf(a) -
                Object.keys(DAYS_WITH_LABELS).indexOf(b)
        )
        .map((day) => DAYS_WITH_LABELS[day].label)
        .join(', ');

    return formattedValue;
};

/**
 * Generates an object containing time values. Can be used to generate values beginning at a specific time, and/or to generate 24 or 48 hours worth of values.
 * @function generateRentalStartsBetweenValues
 * @param {object} [params]
 * @param {string} [params.initialValue] - The initial value to start from as a time string (ie. "00:00", "01:00", etc.)
 * @param {boolean} [params.is48Hours] - Whether or not the returned object contains 48 hours or 24 hours
 * @returns {Object.<string, string>} An object containing time values in the form { "00:00": "12:00 AM", "01:00": "1:00 AM", ... }
 * @example
 * const values = generateRentalStartsBetweenValues();
 * console.log(values);
 * // {
 * //   "00:00": "12:00 AM",
 * //   "01:00": "1:00 AM",
 * //   "02:00": "2:00 AM",
 * //   ...
 * // }
 * @example
 * const values = generateRentalStartsBetweenValues('03:00');
 * console.log(values);
 * // {
 * //   "04:00": "4:00 AM",
 * //   "05:00": "5:00 AM",
 * //   "06:00": "6:00 AM",
 * //   ...
 * // }
 * @example
 * const values = generateRentalStartsBetweenValues({is48Hours: true});
 * console.log(values);
 * // {
 * //   "00:00": "12:00 AM",
 * //   "01:00": "1:00 AM",
 * //   ...
 * //   "47:00": "11:00 PM (next day)",
 * // }
 */
export const generateRentalStartsBetweenValues = ({
    initialValue,
    is48Hours,
} = {}) => {
    const start = initialValue ? Number(initialValue?.split(':')[0]) + 1 : 0;
    const end = is48Hours ? 96 : initialValue && !is48Hours ? 48 : 47;

    return Array.from(
        {length: end - start * 2 + 1},
        (_, i) => (i + start * 2) * 0.5
    ).reduce((acc, duration) => {
        const hours = Math.floor(duration);
        const minutes = duration % 1 > 0 ? '30' : '00';
        const formattedDuration = `${hours
            .toString()
            .padStart(2, '0')}:${minutes}`;
        const formattedTimeString = parse48HourClockTime(formattedDuration);

        return {
            ...acc,
            [formattedDuration]: formattedTimeString,
        };
    }, {});
};

/**
 * Generates an object containing time values. Can be used to generate values beginning at a specific time.
 * @function generateRentalDurationValues
 * @param {string} initialValue - The initail value to start from as a time string (ie. "00:00", "01:00", etc.)
 * @returns {Object.<string, string>} An object containing time values as keys and their corresponding labels as duration values in the form { "00:00": "0 hours", "01:00": "1 hour", "02:00": "2 hours", ... }
 * @example
 * const values = generateRentalDurationValues();
 * console.log(values);
 * // {
 * //   "00:00": "0 hours",
 * //  "00:30": "0.5 hours",
 * //   "01:00": "1 hour",
 * //   "01:30": "1.5 hours",
 * //   "02:00": "2 hours",
 * //   ...
 * // }
 * @example
 * const values = generateRentalDurationValues('03:30');
 * console.log(values);
 * // {
 * //   "03:30": "3.5 hours",
 * //   "04:00": "4 hours",
 * //   "04:30": "4.5 hours",
 * //   "05:00": "5 hours",
 * //   ...
 * // }
 */
export const generateRentalDurationValues = (initialValue) => {
    const startHours = initialValue
        ? parseInt(initialValue.split(':')[0], 10)
        : 0;
    const startMinutes = initialValue
        ? parseInt(initialValue.split(':')[1], 10)
        : 0;
    const start = startHours * 2 + (startMinutes === 30 ? 1 : 0);
    const end = 49;

    return Array.from(
        {length: end - start},
        (_, i) => (i + start) * 0.5
    ).reduce((acc, duration) => {
        const hours = Math.floor(duration).toString();
        const minutes = duration % 1 > 0 ? '30' : '00';
        const formattedDuration = formatDuration(hours, minutes);

        return {
            ...acc,
            [formattedDuration]: `${duration} ${
                duration === 1 ? 'hour' : 'hours'
            }`,
        };
    }, {});
};
